import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ABOUT_INFO, PROJECTS, SKILLS } from '../../../utils/constants';
import { Icon } from '../../Icons';
import { useRouting } from '../../../contexts/RoutingContext';
import styles from './InternetExplorer.module.css';

type Page = 'home' | 'about' | 'projects' | 'skills' | 'contact' | 'error';

interface HistoryEntry {
  page: Page;
  url: string;
}

const PAGE_URLS: Record<Page, string> = {
  home: 'http://www.aditya-portfolio.com/',
  about: 'http://www.aditya-portfolio.com/about',
  projects: 'http://www.aditya-portfolio.com/projects',
  skills: 'http://www.aditya-portfolio.com/skills',
  contact: 'http://www.aditya-portfolio.com/contact',
  error: 'about:error',
};

// Social links
const GITHUB_USERNAME = 'IJustWantAJob';
const LINKEDIN_URL = 'https://linkedin.com/in/Adityakanteti';
const TWITTER_USERNAME = 'AdiCodeMonkey';

const VALID_PAGES: Page[] = ['home', 'about', 'projects', 'skills', 'contact'];

export const InternetExplorer: React.FC = () => {
  const { getInitialSubPath, setSubPath } = useRouting();
  const initializedRef = useRef(false);

  // Initialize from URL sub-path on mount
  const initialPage = (() => {
    const sub = getInitialSubPath('internet-explorer');
    if (sub && VALID_PAGES.includes(sub as Page)) return sub as Page;
    return 'home';
  })();

  const [currentPage, setCurrentPage] = useState<Page>(initialPage);
  const [history, setHistory] = useState<HistoryEntry[]>([{ page: initialPage, url: PAGE_URLS[initialPage] }]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [addressBarValue, setAddressBarValue] = useState(PAGE_URLS[initialPage]);
  const [isLoading, setIsLoading] = useState(false);

  // Sync URL when page changes (skip initial mount)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    setSubPath('internet-explorer', currentPage === 'home' ? null : currentPage);
  }, [currentPage, setSubPath]);

  const navigateTo = useCallback((page: Page) => {
    setIsLoading(true);
    setTimeout(() => {
      const newEntry = { page, url: PAGE_URLS[page] };
      setHistory(prev => [...prev.slice(0, historyIndex + 1), newEntry]);
      setHistoryIndex(prev => prev + 1);
      setCurrentPage(page);
      setAddressBarValue(PAGE_URLS[page]);
      setIsLoading(false);
    }, 300);
  }, [historyIndex]);

  const goBack = useCallback(() => {
    if (historyIndex > 0) {
      setIsLoading(true);
      setTimeout(() => {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCurrentPage(history[newIndex].page);
        setAddressBarValue(history[newIndex].url);
        setIsLoading(false);
      }, 200);
    }
  }, [historyIndex, history]);

  const goForward = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setIsLoading(true);
      setTimeout(() => {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setCurrentPage(history[newIndex].page);
        setAddressBarValue(history[newIndex].url);
        setIsLoading(false);
      }, 200);
    }
  }, [historyIndex, history]);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 500);
  }, []);

  const handleAddressSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const url = addressBarValue.toLowerCase();
    if (url.includes('about')) navigateTo('about');
    else if (url.includes('project')) navigateTo('projects');
    else if (url.includes('skill')) navigateTo('skills');
    else if (url.includes('contact')) navigateTo('contact');
    else if (url.includes('portfolio') || url.includes('home')) navigateTo('home');
    else navigateTo('error');
  }, [addressBarValue, navigateTo]);

  const renderPage = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingPage}>
          <div className={styles.loadingIcon}>
            <Icon name="internet-explorer" size={48} />
          </div>
          <p>Loading...</p>
        </div>
      );
    }

    switch (currentPage) {
      case 'home':
        return (
          <div className={styles.webPage}>
            <nav className={styles.pageNav}>
              <button onClick={() => navigateTo('about')}>About Me</button>
              <button onClick={() => navigateTo('projects')}>Projects</button>
              <button onClick={() => navigateTo('skills')}>Skills</button>
              <button onClick={() => navigateTo('contact')}>Contact</button>
            </nav>
            <main className={styles.pageContent}>
              <div className={styles.welcomeSection}>
                <div className={styles.welcomeAvatar}>
                  <img src="/profile-ie.png" alt={ABOUT_INFO.name} />
                </div>
                <div className={styles.welcomeText}>
                  <h2>Welcome to my Portfolio!</h2>
                  <p>{ABOUT_INFO.bio}</p>
                  <div className={styles.quickLinks}>
                    <h3>Quick Links</h3>
                    <ul>
                      <li><a href="#" onClick={(e) => { e.preventDefault(); navigateTo('projects'); }}>View My Projects</a></li>
                      <li><a href="#" onClick={(e) => { e.preventDefault(); navigateTo('skills'); }}>Technical Skills</a></li>
                      <li><a href="#" onClick={(e) => { e.preventDefault(); navigateTo('contact'); }}>Get in Touch</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </main>
          </div>
        );

      case 'about':
        return (
          <div className={styles.webPage}>
            <header className={styles.pageHeader}>
              <h1>About Me</h1>
            </header>
            <main className={styles.pageContent}>
              <div className={styles.aboutSection}>
                <img src="/profile-about.jpg" alt={ABOUT_INFO.name} className={styles.aboutPhoto} />
                <div className={styles.aboutInfo}>
                  <h2>{ABOUT_INFO.name}</h2>
                  <h3>{ABOUT_INFO.title}</h3>
                  <p>{ABOUT_INFO.bio}</p>
                  <p><strong>Location:</strong> {ABOUT_INFO.location}</p>
                  <p><strong>Email:</strong> {ABOUT_INFO.email}</p>
                </div>
              </div>
              <div className={styles.backLink}>
                <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>← Back to Home</a>
              </div>
            </main>
          </div>
        );

      case 'projects':
        return (
          <div className={styles.webPage}>
            <header className={styles.pageHeader}>
              <h1>My Projects</h1>
            </header>
            <main className={styles.pageContent}>
              <div className={styles.projectsGrid}>
                {PROJECTS.map(project => (
                  <div key={project.id} className={styles.projectCard}>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                    <div className={styles.techTags}>
                      {project.technologies.map(tech => (
                        <span key={tech} className={styles.techTag}>{tech}</span>
                      ))}
                    </div>
                    <div className={styles.projectLinks}>
                      {project.liveUrl && <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">Live Demo</a>}
                      {project.githubUrl && <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">GitHub</a>}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.backLink}>
                <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>← Back to Home</a>
              </div>
            </main>
          </div>
        );

      case 'skills':
        return (
          <div className={styles.webPage}>
            <header className={styles.pageHeader}>
              <h1>Technical Skills</h1>
            </header>
            <main className={styles.pageContent}>
              <div className={styles.skillsSection}>
                {['frontend', 'backend', 'tools'].map(category => (
                  <div key={category} className={styles.skillCategory}>
                    <h3>{category.charAt(0).toUpperCase() + category.slice(1)}</h3>
                    <div className={styles.skillsList}>
                      {SKILLS.filter(s => s.category === category).map(skill => (
                        <div key={skill.name} className={styles.skillItem}>
                          <span className={styles.skillName}>{skill.name}</span>
                          <div className={styles.skillBar}>
                            <div className={styles.skillFill} style={{ width: `${skill.level}%` }} />
                          </div>
                          <span className={styles.skillLevel}>{skill.level}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.backLink}>
                <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>← Back to Home</a>
              </div>
            </main>
          </div>
        );

      case 'contact':
        return (
          <div className={styles.webPage}>
            <header className={styles.pageHeader}>
              <h1>Contact Me</h1>
            </header>
            <main className={styles.pageContent}>
              <div className={styles.contactInfo}>
                <h3>Get in Touch</h3>
                <p><strong>Email:</strong> <a href={`mailto:${ABOUT_INFO.email}`}>{ABOUT_INFO.email}</a></p>
                <p><strong>Location:</strong> {ABOUT_INFO.location}</p>

                <div className={styles.socialLinks}>
                  <h3>Connect with me:</h3>
                  <div className={styles.socialIcons}>
                    <a
                      href={`https://github.com/${GITHUB_USERNAME}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      title="GitHub"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                      </svg>
                    </a>
                    <a
                      href={LINKEDIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      title="LinkedIn"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a
                      href={`https://twitter.com/${TWITTER_USERNAME}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      title="Twitter/X"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              <div className={styles.backLink}>
                <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>← Back to Home</a>
              </div>
            </main>
          </div>
        );

      case 'error':
        return (
          <div className={styles.errorPage}>
            <div className={styles.errorIcon}>✖</div>
            <h1>The page cannot be displayed</h1>
            <p>The page you are looking for is currently unavailable.</p>
            <hr />
            <p>Please try the following:</p>
            <ul>
              <li>Click the <button onClick={refresh}>Refresh</button> button, or try again later.</li>
              <li>If you typed the page address, check the spelling.</li>
              <li>Visit the <a href="#" onClick={(e) => { e.preventDefault(); navigateTo('home'); }}>homepage</a>.</li>
            </ul>
          </div>
        );
    }
  };

  return (
    <div className={styles.ie}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarButtons}>
          <button
            className={styles.navButton}
            onClick={goBack}
            disabled={historyIndex === 0}
            title="Back"
          >
            <span className={styles.navIcon}>◀</span>
            <span className={styles.navLabel}>Back</span>
          </button>
          <button
            className={styles.navButton}
            onClick={goForward}
            disabled={historyIndex >= history.length - 1}
            title="Forward"
          >
            <span className={styles.navIcon}>▶</span>
          </button>
          <button
            className={styles.navButton}
            onClick={() => navigateTo('home')}
            title="Home"
          >
            <span className={styles.navIcon}>🏠</span>
          </button>
          <button
            className={styles.navButton}
            onClick={refresh}
            title="Refresh"
          >
            <span className={styles.navIcon}>🔄</span>
          </button>
        </div>
      </div>

      {/* Address bar */}
      <div className={styles.addressBar}>
        <span className={styles.addressLabel}>Address</span>
        <form onSubmit={handleAddressSubmit} className={styles.addressForm}>
          <div className={styles.addressInputContainer}>
            <Icon name="internet-explorer" size={16} />
            <input
              type="text"
              value={addressBarValue}
              onChange={(e) => setAddressBarValue(e.target.value)}
              className={styles.addressInput}
            />
          </div>
          <button type="submit" className={styles.goButton}>Go</button>
        </form>
      </div>

      {/* Content area */}
      <div className={styles.content}>
        {renderPage()}
      </div>

      {/* Status bar */}
      <div className={styles.statusBar}>
        <span className={styles.status}>
          {isLoading ? 'Opening page...' : 'Done'}
        </span>
        <span className={styles.zone}>Internet</span>
      </div>
    </div>
  );
};

export default InternetExplorer;
